import clsx from "clsx";
import React, {
  DetailedHTMLProps,
  forwardRef,
  InputHTMLAttributes,
  PropsWithChildren,
} from "react";

import { Icon } from "../../../icon";
import { FormFieldContainer } from "../form-field/form-field-container";
import { FormFieldMessage } from "../form-field/form-field-message";
import { FormFieldMessageProps, FormFieldStatus } from "../form-field/types";

export interface CheckboxProps
  extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    FormFieldMessageProps {
  optionalLabel?: string;
  collapseMessage?: boolean;
  labelPosition?: "right" | "left";
  checkboxAlignment?: "center" | "top";
  disableLabelDecorations?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, PropsWithChildren<CheckboxProps>>(
  (
    {
      message,
      status = FormFieldStatus.Info,
      required,
      children,
      className,
      optionalLabel,
      collapseMessage,
      labelPosition = "right",
      checkboxAlignment = "center",
      disableLabelDecorations = false,
      ...rest
    },
    ref
  ) => {
    const { disabled, defaultChecked } = rest;

    return (
      <FormFieldContainer disabled={disabled} className={className}>
        <label
          className={clsx(
            "w-full flex gap-2",
            checkboxAlignment === "center" && "items-center",
            !disabled && "cursor-pointer",
            labelPosition === "left" && "flex-row-reverse"
          )}
        >
          <div className="relative self-start">
            <input
              className={clsx(
                "peer relative appearance-none shrink-0 w-[18px] h-[18px]",
                "border-2 border-neutral-dark rounded-sm mt-1 cursor-pointer",
                "checked:bg-primary-light checked:border-primary"
              )}
              type="checkbox"
              required={required}
              disabled={disabled}
              defaultChecked={defaultChecked}
              {...rest}
              ref={ref}
            />
            <Icon
              icon="checkbox-check"
              className={clsx(
                "w-[12px] absolute top-[1px] left-[3px] pointer-events-none",
                "hidden peer-checked:block outline-none"
              )}
            />
          </div>
          <span className="select-none flex items-center mt-[2px]">
            {children}
            {/* If optional and optionalLabel is defined, show optional label */}
            {!disableLabelDecorations && !required && optionalLabel && children && (
              <span>&nbsp;{optionalLabel}</span>
            )}
            {/* If required and no optional label is defined, show asterisk */}
            {!disableLabelDecorations && !optionalLabel && required && children && <span>*</span>}
          </span>
        </label>

        {(message || !collapseMessage) && <FormFieldMessage message={message} status={status} />}
      </FormFieldContainer>
    );
  }
);
